import React, { useState, useEffect } from "react";
import styles from "../../gmanewstyle.module.css"; // USE YOUR GLOBAL STYLE FILE
import { format } from "date-fns";

const LogATicketModal = ({
  open,
  onClose,
  role, // "owner" OR "trustee" passed in from portal button
}) => {
  /* ─────────────────────────────────────────────
      STATE — identical to old LogATicketPage
  ───────────────────────────────────────────── */
  const [userRole, setUserRole] = useState(role || "");
  const [ticketPurpose, setTicketPurpose] = useState("Request");
  const [ticketType, setTicketType] = useState("");
  const [entityList, setEntityList] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState("");
  const [selectedBuilding, setSelectedBuilding] = useState("");
  const [buildingList, setBuildingList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [formFields, setFormFields] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
  });
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [description, setDescription] = useState("");

  /* ─────────────────────────────────────────────
      LOAD BUILDINGS FOR OWNER OR TRUSTEE
  ───────────────────────────────────────────── */
  useEffect(() => {
    if (!userRole) return;

    const key =
      userRole === "trustee" ? "trustee_buildings" : "owner_buildings";
    const cached = localStorage.getItem(key);

    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setBuildingList(parsed || []);

        if (parsed.length === 1 && ticketPurpose === "Request") {
          const id = parsed[0].cj_gmabuildingid || parsed[0].buildingGuid;
          setSelectedBuilding(id);
        }
      } catch (err) {
        console.error("Error parsing cached buildings:", err);
      }
    }
  }, [userRole, ticketPurpose]);

  /* ─────────────────────────────────────────────
      AUTO SELECT building if only 1
  ───────────────────────────────────────────── */
  useEffect(() => {
    if (
      ticketPurpose === "Request" &&
      buildingList.length === 1 &&
      !selectedBuilding
    ) {
      const id =
        buildingList[0].cj_gmabuildingid || buildingList[0].buildingGuid;
      setSelectedBuilding(id);
    }
  }, [ticketPurpose, buildingList, selectedBuilding]);

  /* ─────────────────────────────────────────────
      LOAD ENTITY LIST BASED ON ticketType & Role
  ───────────────────────────────────────────── */
  useEffect(() => {
    if (!ticketType || !userRole) return;

    const keyMap = {
      trustee: {
        Building: "trustee_buildings",
        "Active Individual Approval": "trustee_active_individual_approvals",
        "Historical Individual Approval":
          "trustee_history_individual_approvals",
        Payment: "trustee_payments",
        Maintenance: "trustee_maintenances",
        "Public Task": "trustee_public_tasks",
        "Bank Account": "trustee_bank_accounts",
        AOD: "trustee_aods",
        Disconnection: "trustee_disconnections",
        Legal: "trustee_legals",
        Other: "trustee_other",
        "Levy Account": "trustee_balances",
      },
      owner: {
        Account: "owner_accounts",
        "Levy Account": "owner_levy_accounts",
        Building: "owner_buildings",
        Other: "owner_other",
      },
    };

    const key = keyMap[userRole]?.[ticketType];
    if (!key) return;

    const cached = localStorage.getItem(key);
    if (!cached) return setEntityList([]);

    try {
      const parsed = JSON.parse(cached);
      setEntityList(parsed || []);
    } catch (err) {
      console.error("Failed to parse entity list:", err);
      setEntityList([]);
    }
  }, [ticketType, userRole]);

  /* ─────────────────────────────────────────────
      SUBMIT TICKET (unchanged)
  ───────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    const contactId = localStorage.getItem("contact_id");
    const userprofileId = localStorage.getItem("userProfileId");

    if (
      !token ||
      !ticketType ||
      !description.trim() ||
      !contactId ||
      !userprofileId ||
      (ticketPurpose === "Request" && !selectedBuilding) ||
      (ticketPurpose === "Report" && ticketType !== "Other" && !selectedEntity)
    ) {
      setError("Please complete all fields.");
      return;
    }

    const payload = {
      ...formFields,
      ticket_type: ticketType,
      role: userRole,
      description,
      contact_id: contactId,
      userprofile_id: userprofileId,
      linked_id:
        ticketPurpose === "Request" ? selectedBuilding : selectedEntity,
      ticket_purpose: ticketPurpose,
    };

    try {
      const res = await fetch(
        "https://portal.gmacc.co.za/api/tickets/create/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (res.ok) {
        setSuccessMessage("✅ Ticket submitted successfully.");
        setTimeout(() => setSuccessMessage(""), 4000);
        setError("");
        setSelectedEntity("");
        setSelectedBuilding("");
        setDescription("");
        setFormFields({ firstName: "", lastName: "", mobile: "", email: "" });
        setTicketType("");
        setSearchTerm("");
        setTicketPurpose("Request");
      } else {
        setError(data.error || "❌ Submission failed.");
      }
    } catch (err) {
      setError("❌ Error submitting ticket.");
    }
  };

  /* ─────────────────────────────────────────────
      STOP RENDERING IF MODAL CLOSED
  ───────────────────────────────────────────── */
  if (!open) return null;

  /* ─────────────────────────────────────────────
      RENDER MODAL
  ───────────────────────────────────────────── */
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className={styles.modalHeaderRow}>
          <h2 className={styles.modalTitle}>TICKET INFORMATION</h2>
          <button className={styles.modalClose} onClick={onClose}>
            ✕
          </button>
        </div>

        {/* BODY */}
        <div className={styles.modalBody}>
          {error && <p className={styles.errorText}>{error}</p>}
          {successMessage && (
            <p className={styles.successText}>{successMessage}</p>
          )}

          <form onSubmit={handleSubmit}>
            {/* PURPOSE SELECT 
        <div className={styles.inputGroup}>
          <label>Ticket Purpose</label>
          <select
            value={ticketPurpose}
            onChange={(e) => setTicketPurpose(e.target.value)}
            className={styles.selectPill}
          >
            <option value="Request">Request</option>
            <option value="Report">Report</option>
          </select>
        </div> */}

            {/* REQUEST MODE */}
            {ticketPurpose === "Request" && (
              <>
                {/* BUILDING */}
                <div className={styles.inputGroup}>
                  <label>Select Building</label>
                  <select
                    required
                    value={selectedBuilding}
                    onChange={(e) => setSelectedBuilding(e.target.value)}
                    className={styles.selectPill}
                  >
                    <option value=""> </option>
                    {buildingList.map((b, i) => {
                      const id = b.cj_gmabuildingid || b.buildingGuid;
                      const label = b.name || b.building || "Unnamed Building";
                      return (
                        <option key={i} value={id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* TICKET TYPE */}
                <div className={styles.inputGroup}>
                  <label>Select Ticket Type</label>
                  <select
                    required
                    value={ticketType}
                    onChange={(e) => setTicketType(e.target.value)}
                    className={styles.selectPill}
                  >
                    <option value=""> </option>
                    {[
                      "User Profile",
                      "Account Query",
                      "Maintenance",
                      "Code Of Conduct",
                      "Property Transfers",
                      "Insurance",
                      "Service Complaint",
                    ].map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* REPORT MODE */}
            {ticketPurpose === "Report" && (
              <>
                <div className={styles.inputGroup}>
                  <label>Select Ticket Type</label>
                  <select
                    required
                    value={ticketType}
                    onChange={(e) => setTicketType(e.target.value)}
                    className={styles.selectPill}
                  >
                    <option value=""> </option>

                    {userRole === "trustee" &&
                      [
                        "Building",
                        "Active Individual Approval",
                        "Historical Individual Approval",
                        "Payment",
                        "Maintenance",
                        "Levy Account",
                        "Public Task",
                        "Bank Account",
                        "AOD",
                        "Disconnection",
                        "Legal",
                        "Other",
                      ].map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}

                    {userRole === "owner" &&
                      ["Building", "Account", "Levy Account", "Other"].map(
                        (t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        )
                      )}
                  </select>
                </div>

                {/* Filter field */}
                {ticketType && ticketType !== "Other" && (
                  <>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        placeholder="Filter List..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.selectPill}
                      />
                    </div>

                    {/* Select linked item */}
                    <div className={styles.inputGroup}>
                      <label>Select Linked Item</label>
                      <select
                        value={selectedEntity}
                        onChange={(e) => setSelectedEntity(e.target.value)}
                        className={styles.selectPill}
                      >
                        <option value="">Select an option</option>

                        {entityList
                          .map((item) => {
                            const label = (() => {
                              switch (ticketType) {
                                case "Payment":
                                  return item.cr446_paymentpk;
                                case "Active Individual Approval":
                                case "Historical Individual Approval":
                                  return item.subject || item.cj_name;
                                case "Public Task":
                                  return item.cj_subjectt;
                                case "Maintenance":
                                  return item.subject || item.cj_name;
                                case "AOD":
                                  return item[
                                    "_cj_linkedaccount_value@OData.Community.Display.V1.FormattedValue"
                                  ];
                                case "Building":
                                  return item.building || item.name;
                                case "Bank Account":
                                  return (
                                    item.cj_bankaccountname ||
                                    item.cj_accountnumber
                                  );
                                case "Account":
                                  return item.owner_account_name;
                                case "Levy Account":
                                  return (
                                    item.levy_name || item.cj_sageaccountnumber
                                  );
                                case "Legal":
                                  return item.cr446_textaccountnumber;
                                case "Disconnection":
                                  return item[
                                    "_cj_accountnumber_value@OData.Community.Display.V1.FormattedValue"
                                  ];
                                default:
                                  return (
                                    item.name || item.subject || item.cj_name
                                  );
                              }
                            })();

                            const value = (() => {
                              switch (ticketType) {
                                case "Payment":
                                  return item.cr446_paymentid;
                                case "Active Individual Approval":
                                case "Historical Individual Approval":
                                  return item.cj_individualapprovalid;
                                case "Public Task":
                                  return item.cj_publictasksid;
                                case "Maintenance":
                                  return item.cj_maintenancebid;
                                case "AOD":
                                  return item.cj_aodid;
                                case "Legal":
                                  return item.cj_legalid;
                                case "Disconnection":
                                  return item.cj_disconnectionid;
                                case "Building":
                                  return (
                                    item.buildingGuid || item.cj_gmabuildingid
                                  );
                                case "Bank Account":
                                  return item.cj_gmabankaccountid;
                                case "Account":
                                  return item.accountid || item.id;
                                case "Levy Account":
                                  return item.cj_gmaaccountnumberid || item.id;
                                default:
                                  return item.id || "";
                              }
                            })();

                            return { label, value };
                          })
                          .filter((i) =>
                            i.label
                              ?.toLowerCase()
                              .includes(searchTerm.toLowerCase())
                          )
                          .map((i, idx) => (
                            <option key={idx} value={i.value}>
                              {i.label}
                            </option>
                          ))}
                      </select>
                    </div>
                  </>
                )}
              </>
            )}

            {/* DESCRIPTION */}
            <div className={styles.inputGroup} style={{ marginTop: "12px" }}>
              <label>Describe your issue</label>
              <textarea
                required
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={styles.inputLongTextarea}
              />
            </div>

            {/* FOOTER BUTTONS */}
            <div className={styles.modalActions}>
              <button type="submit" className={styles.primaryButton}>
                Submit
              </button>
              <button
                type="button"
                className={styles.modalBtnSecondary}
                onClick={onClose}
              >
                Back
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LogATicketModal;
